import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  CalendarBlank,
  Info,
  Microphone,
  Note,
  Phone,
} from "@phosphor-icons/react";

const NOTE_TYPES = [
  {
    value: "note",
    label: "Information",
    icon: Info,
  },
  {
    value: "meeting",
    label: "Réunion",
    icon: CalendarBlank,
  },
  {
    value: "call",
    label: "Appel",
    icon: Phone,
  },
];

const VISIBLE_TYPES = new Set([
  "note",
  "complementary_info",
  "meeting",
  "call",
]);

function noteType(note = {}) {
  return (
    note?.activityType ||
    note?.activity_type ||
    "note"
  );
}

function noteContent(note = {}) {
  return String(
    note?.content ||
    note?.note ||
    ""
  ).trim();
}

function noteDate(note = {}) {
  return (
    note?.activityDate ||
    note?.createdAt ||
    note?.updatedAt ||
    ""
  );
}

function noteMetadata(type) {
  if (type === "meeting") {
    return {
      label: "Réunion",
      icon: CalendarBlank,
    };
  }

  if (type === "call") {
    return {
      label: "Appel",
      icon: Phone,
    };
  }

  return {
    label: "Information",
    icon: Info,
  };
}

function formatDateTime(value) {
  if (!value) {
    return "Date non renseignée";
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return "Date non renseignée";
  }

  return new Intl.DateTimeFormat(
    "fr-FR",
    {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  ).format(parsed);
}

function normalizeNotes(notes) {
  return [...notes]
    .filter((note) =>
      VISIBLE_TYPES.has(
        noteType(note)
      )
    )
    .filter((note) =>
      Boolean(noteContent(note))
    )
    .sort((left, right) => {
      const leftDate =
        new Date(
          noteDate(left) || 0
        ).getTime();

      const rightDate =
        new Date(
          noteDate(right) || 0
        ).getTime();

      return rightDate - leftDate;
    });
}

function TypeSelector({
  value,
  disabled,
  onChange,
}) {
  return (
    <div
      className="flex flex-wrap gap-2"
      data-testid="contact-information-types"
    >
      {NOTE_TYPES.map(
        ({
          value: option,
          label,
          icon: Icon,
        }) => {
          const selected =
            option === value;

          return (
            <button
              key={option}
              type="button"
              disabled={disabled}
              aria-pressed={selected}
              onClick={() =>
                onChange(option)
              }
              className={[
                "inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border px-3 py-2 text-sm font-bold transition",
                selected
                  ? "border-[var(--do-accent)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]"
                  : "border-[var(--do-border)] bg-[var(--do-bg-solid)] text-[var(--do-ink)] hover:border-[var(--do-border-strong)]",
                disabled
                  ? "cursor-not-allowed opacity-50"
                  : "",
              ].join(" ")}
            >
              <Icon size={17} />
              {label}
            </button>
          );
        }
      )}
    </div>
  );
}

function InformationCard({
  note,
  selected,
  onSelect,
}) {
  const metadata =
    noteMetadata(noteType(note));

  const Icon = metadata.icon;

  return (
    <button
      type="button"
      onClick={() =>
        onSelect(note)
      }
      className={[
        "w-full rounded-2xl border p-3 text-left transition sm:p-4",
        selected
          ? "border-[var(--do-accent)] bg-[var(--do-accent-soft)]"
          : "border-[var(--do-border)] bg-[var(--do-bg-solid)] hover:border-[var(--do-border-strong)]",
      ].join(" ")}
      data-testid="contact-information-note"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[var(--do-bg-2)] text-[var(--do-accent)]">
          <Icon size={18} />
        </span>

        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-sm font-black text-[var(--do-ink)]">
              {metadata.label}
            </span>

            <span className="text-xs text-[var(--do-muted)]">
              {formatDateTime(
                noteDate(note)
              )}
            </span>
          </span>

          <span className="mt-1 block text-xs text-[var(--do-muted)]">
            {note.authorName ||
              "Utilisateur"}
          </span>

          <span
            className="mt-2 block overflow-hidden text-sm leading-5 text-[var(--do-ink)]"
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient:
                "vertical",
            }}
          >
            {noteContent(note)}
          </span>
        </span>
      </div>
    </button>
  );
}

function SelectedInformation({
  note,
  saving = false,
  onUpdate,
  onDelete,
}) {
  // ACTA_INFORMATION_NOTE_EDITOR_HOOKS_V1
  // Les hooks restent toujours appelés dans le même ordre,
  // même lorsqu'aucune note n'est sélectionnée.
  const [
    editing,
    setEditing,
  ] = useState(false);

  const [
    editValue,
    setEditValue,
  ] = useState(
    noteContent(note)
  );

  useEffect(() => {
    setEditing(false);
    setEditValue(
      noteContent(note)
    );
  }, [note?.id]);

  if (!note) {
    return (
      <div className="rounded-2xl border border-dashed border-[var(--do-border)] bg-[var(--do-bg-solid)] p-5 text-sm text-[var(--do-muted)]">
        Sélectionnez une note pour lire son contenu complet.
      </div>
    );
  }

  const metadata =
    noteMetadata(noteType(note));

  const Icon = metadata.icon;

  const saveEdit = async () => {
    const success =
      await onUpdate?.(
        note,
        editValue
      );

    if (success) {
      setEditing(false);
    }
  };

  return (
    <article
      className="rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4 sm:p-5"
      data-testid="contact-information-selected-note"
    >
      <div className="flex items-center gap-2">
        <Icon
          size={18}
          className="text-[var(--do-accent)]"
        />

        <span className="text-sm font-black text-[var(--do-accent)]">
          {metadata.label}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap gap-x-2 gap-y-1 text-xs text-[var(--do-muted)]">
        <span>
          {formatDateTime(
            noteDate(note)
          )}
        </span>

        <span>·</span>

        <span>
          {note.authorName ||
            "Utilisateur"}
        </span>
      </div>

      {editing ? (
        <div className="mt-4 border-t border-[var(--do-border)] pt-4">
          <textarea
            rows={6}
            maxLength={2000}
            value={editValue}
            disabled={saving}
            onChange={(event) =>
              setEditValue(
                event.target.value
              )
            }
            className="min-h-[150px] w-full resize-y rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-3 text-sm leading-6 text-[var(--do-ink)] outline-none focus:border-[var(--do-accent)]"
            data-testid="contact-information-edit-input"
          />

          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={
                saving ||
                !editValue.trim()
              }
              onClick={saveEdit}
              className="min-h-[44px] rounded-xl bg-[var(--do-accent)] px-4 text-sm font-black text-[var(--do-on-accent)] disabled:opacity-50"
              data-testid="contact-information-edit-save"
            >
              Enregistrer
            </button>

            <button
              type="button"
              disabled={saving}
              onClick={() => {
                setEditValue(
                  noteContent(note)
                );
                setEditing(false);
              }}
              className="min-h-[44px] rounded-xl border border-[var(--do-border)] px-4 text-sm font-bold text-[var(--do-ink)]"
            >
              Annuler
            </button>
          </div>
        </div>
      ) : (
        <>
          <p className="mt-4 whitespace-pre-wrap break-words border-t border-[var(--do-border)] pt-4 text-sm leading-6 text-[var(--do-ink)] sm:text-[15px]">
            {noteContent(note)}
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={saving}
              onClick={() => {
                setEditValue(
                  noteContent(note)
                );
                setEditing(true);
              }}
              className="min-h-[44px] rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 text-sm font-black text-[var(--do-ink)] disabled:opacity-50"
              data-testid="contact-information-edit"
            >
              Modifier
            </button>

            <button
              type="button"
              disabled={saving}
              onClick={() =>
                onDelete?.(note)
              }
              className="min-h-[44px] rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-4 text-sm font-black text-[var(--do-accent)] disabled:opacity-50"
              data-testid="contact-information-delete"
            >
              Supprimer
            </button>
          </div>
        </>
      )}
    </article>
  );
}

export default function ContactInformationPanel({
  notes = [],
  value = "",
  type = "note",
  saving = false,
  meetingReportMode = false,
  dictationSupported = false,
  dictationListening = false,
  onDictationToggle,
  onChange,
  onTypeChange,
  onSave,
  onUpdateNote,
  onDeleteNote,
}) {
  const normalizedNotes =
    useMemo(
      () => normalizeNotes(notes),
      [notes]
    );

  const [
    selectedNoteId,
    setSelectedNoteId,
  ] = useState("");

  const [
    showAll,
    setShowAll,
  ] = useState(false);

  useEffect(() => {
    if (
      selectedNoteId &&
      normalizedNotes.some(
        (note) =>
          String(note.id) ===
          String(selectedNoteId)
      )
    ) {
      return;
    }

    setSelectedNoteId(
      normalizedNotes[0]?.id || ""
    );
  }, [
    normalizedNotes,
    selectedNoteId,
  ]);

  const selectedNote =
    normalizedNotes.find(
      (note) =>
        String(note.id) ===
        String(selectedNoteId)
    ) ||
    normalizedNotes[0] ||
    null;

  const visibleNotes =
    showAll
      ? normalizedNotes
      : normalizedNotes.slice(0, 6);

  const canSave =
    Boolean(value.trim()) &&
    !saving;

  const submit = () => {
    if (canSave) {
      onSave();
    }
  };

  return (
    <section
      className="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1.15fr)_minmax(300px,0.9fr)_minmax(280px,0.8fr)]"
      data-testid="contact-information-panel"
    >
      <article className="min-w-0 rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-1)] p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[var(--do-accent-soft)] text-[var(--do-accent)]">
            <Info size={19} />
          </span>

          <div className="min-w-0">
            <h2 className="font-heading text-base font-black text-[var(--do-ink)] sm:text-lg">
              Informations complémentaires
            </h2>

            <p className="mt-1 text-sm leading-5 text-[var(--do-muted)]">
              {meetingReportMode
                ? "Ajoutez le compte rendu de ce rendez-vous. Vous pouvez écrire ou dicter depuis cet appareil."
                : "Ajoutez un compte rendu de réunion, un appel ou toute information utile sur ce contact."}
            </p>
          </div>
        </div>

        {meetingReportMode ? (
          <div
            className="mt-5 inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-[var(--do-accent)] bg-[var(--do-accent-soft)] px-3 py-2 text-sm font-bold text-[var(--do-accent)]"
            data-testid="contact-information-meeting-report-type"
          >
            <CalendarBlank size={17} />
            Compte rendu de rendez-vous
          </div>
        ) : (
          <div className="mt-5">
            <div className="mb-2 text-xs font-bold uppercase tracking-wide text-[var(--do-muted)]">
              Type de note
            </div>

            <TypeSelector
              value={type}
              disabled={saving}
              onChange={onTypeChange}
            />
          </div>
        )}

        <label className="mt-4 block">
          <span className="sr-only">
            Information complémentaire
          </span>

          <textarea
            rows={7}
            maxLength={2000}
            value={value}
            disabled={saving}
            onChange={(event) =>
              onChange(
                event.target.value
              )
            }
            onKeyDown={(event) => {
              if (
                (
                  event.ctrlKey ||
                  event.metaKey
                ) &&
                event.key ===
                  "Enter"
              ) {
                event.preventDefault();
                submit();
              }
            }}
            placeholder={
              meetingReportMode
                ? "Résumez l’échange avec le prospect…"
                : "Ex. Rendez-vous du 27/08 avec Mme Martin. Le client souhaite étudier une nouvelle machine…"
            }
            className="mt-1 min-h-[180px] w-full resize-y rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-3 text-base leading-6 text-[var(--do-ink)] outline-none transition placeholder:text-[var(--do-muted)] focus:border-[var(--do-accent)] sm:min-h-[210px] sm:text-sm"
            data-testid="contact-information-input"
          />
        </label>

        <div className="mt-2 flex items-center justify-between gap-3">
          <span className="hidden text-xs text-[var(--do-muted)] sm:inline">
            Ctrl/⌘ + Entrée pour enregistrer
          </span>

          <span className="ml-auto text-xs tabular-nums text-[var(--do-muted)]">
            {value.length}/2000
          </span>
        </div>

        {meetingReportMode ? (
          <div className="mt-4">
            {dictationSupported ? (
              <button
                type="button"
                disabled={saving}
                onClick={onDictationToggle}
                className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-2.5 text-sm font-bold text-[var(--do-ink)] transition hover:border-[var(--do-border-strong)] disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="contact-information-dictation"
              >
                <Microphone
                  size={17}
                  weight={
                    dictationListening
                      ? "fill"
                      : "regular"
                  }
                />

                {dictationListening
                  ? "Arrêter la dictée"
                  : "Dicter le compte rendu"}
              </button>
            ) : (
              <p
                className="text-xs text-[var(--do-muted)]"
                data-testid="contact-information-dictation-unavailable"
              >
                La dictée vocale n’est pas disponible dans ce navigateur.
              </p>
            )}
          </div>
        ) : null}

        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            disabled={!canSave}
            onClick={submit}
            className="inline-flex min-h-[44px] items-center justify-center rounded-xl bg-[var(--do-accent-strong)] px-4 py-2.5 text-sm font-black text-[var(--do-on-accent)] disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="contact-information-save"
          >
            {saving
              ? "Enregistrement…"
              : "Enregistrer"}
          </button>

          <button
            type="button"
            disabled={
              saving ||
              !value
            }
            onClick={() =>
              onChange("")
            }
            className="inline-flex min-h-[44px] items-center justify-center rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-2.5 text-sm font-bold text-[var(--do-ink)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Vider
          </button>
        </div>
      </article>

      <article className="min-w-0 rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-1)] p-4 sm:p-5">
        <div className="flex items-center gap-2">
          <Note
            size={19}
            className="text-[var(--do-accent)]"
          />

          <h2 className="font-heading text-base font-black text-[var(--do-ink)]">
            Dernières notes
          </h2>
        </div>

        {visibleNotes.length ? (
          <div className="mt-4 space-y-2">
            {visibleNotes.map(
              (note) => (
                <InformationCard
                  key={note.id}
                  note={note}
                  selected={
                    String(
                      selectedNote?.id
                    ) ===
                    String(note.id)
                  }
                  onSelect={
                    (item) =>
                      setSelectedNoteId(
                        item.id
                      )
                  }
                />
              )
            )}
          </div>
        ) : (
          <div className="mt-4 rounded-2xl border border-dashed border-[var(--do-border)] bg-[var(--do-bg-solid)] p-5 text-center text-sm text-[var(--do-muted)]">
            Aucune information enregistrée.
          </div>
        )}

        {normalizedNotes.length > 6 && (
          <button
            type="button"
            onClick={() =>
              setShowAll(
                (current) =>
                  !current
              )
            }
            className="mt-3 min-h-[44px] text-sm font-black text-[var(--do-accent)]"
          >
            {showAll
              ? "Réduire"
              : `Voir toutes les notes (${normalizedNotes.length})`}
          </button>
        )}
      </article>

      <article className="min-w-0 rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-1)] p-4 sm:p-5">
        <h2 className="font-heading text-base font-black text-[var(--do-ink)]">
          Note sélectionnée
        </h2>

        <div className="mt-4">
          <SelectedInformation
            note={selectedNote}
            saving={saving}
            onUpdate={onUpdateNote}
            onDelete={onDeleteNote}
          />
        </div>
      </article>
    </section>
  );
}
